/**
 * Which platform is looking at us, for the two questions the install flow has to answer:
 * "can this device install at all" and "is it already installed".
 *
 * Pure, no React, no side effects — the whole file is safe to call during render.
 */

/** True on iPhone, iPod, and iPadOS 13+, which reports itself as a Mac with a touchscreen. */
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod|iPad/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * Running as an installed app rather than in a browser tab.
 *
 * The `matchMedia` guard is load-bearing, not defensive: jsdom does not implement it and
 * `src/test/polyfills.ts` has no stub, so an unguarded call throws in every test that
 * imports this module. `navigator.standalone` is the iOS-only equivalent.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const byDisplayMode =
    typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  const byIosFlag = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return byDisplayMode || byIosFlag;
}

/**
 * Safari on iOS, as opposed to Chrome or Firefox on iOS.
 *
 * Worth distinguishing because only Safari's share sheet is a reliable "Zum Home-Bildschirm"
 * path, and an app installed from a third-party iOS browser gets its own storage anyway.
 */
export function isIosSafari(): boolean {
  if (!isIos()) return false;
  const ua = navigator.userAgent;
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}
